<template>
  <div class="recent-chats">
    <button
      type="button"
      class="recent-header"
      :aria-expanded="recentExpanded"
      @click="toggleRecentChats"
    >
      <span class="recent-header-text">Recent Chats</span>

      <v-icon
        size="18"
        class="recent-chevron"
      >
        {{
          recentExpanded
            ? 'mdi-chevron-down'
            : 'mdi-chevron-right'
        }}
      </v-icon>
    </button>

    <div class="recent-body">
      <div
        v-if="loading"
        class="loading-container"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="20"
          width="2"
        />
      </div>

      <div
        v-if="!loading && recentExpanded"
        class="chat-list"
      >
        <div
          v-for="session in sessions"
          :key="session.sessionId"
          :class="[
            'chat-item',
            {'chat-item--active': isSelected(session)}
          ]"
          :style="{pointerEvents: sessionSelectionDisabled ? 'none' : 'auto'}"
          :tabindex="sessionSelectionDisabled ? -1 : 0"
          role="button"
          :aria-disabled="sessionSelectionDisabled ? 'true' : 'false'"
          @click="sessionSelectionDisabled || $emit('select-session', session)"
          @keydown.enter="sessionSelectionDisabled || $emit('select-session', session)"
          @keydown.space.prevent="sessionSelectionDisabled || $emit('select-session', session)"
        >
          <div class="chat-title-col">
            <span
              class="chat-title"
              @mouseenter="onTitleMouseEnter"
              @mouseleave="onTitleMouseLeave"
            >
              <span class="chat-title-inner">{{ session.title }}</span>
            </span>
          </div>
          <div class="chat-delete-col">
            <button
              type="button"
              class="chat-delete-btn"
              aria-label="Delete chat"
              @click.stop="$emit('delete-session', session)"
            >
              <v-icon
                size="18"
                class="chat-delete-icon"
              >
                mdi-trash-can-outline
              </v-icon>
            </button>
          </div>
        </div>

        <div
          v-if="sessions.length === 0"
          class="empty-state"
        >
          No recent chats
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RecentChats',

  props: {
    sessions: {
      type   : Array,
      default: () => []
    },

    selectedSession: {
      type   : Object,
      default: null
    },

    loading: {
      type   : Boolean,
      default: false
    },

    sessionSelectionDisabled: {
      type   : Boolean,
      default: false
    }
  },

  data () {
    return {
      recentExpanded: true
    }
  },

  methods: {
    isSelected (session) {
      return Boolean(
        this.selectedSession &&
          this.selectedSession.sessionId === session.sessionId
      )
    },

    toggleRecentChats () {
      this.recentExpanded = !this.recentExpanded
    },

    onTitleMouseEnter (event) {
      const outer = event.currentTarget
      const inner = outer.querySelector('.chat-title-inner')

      this.cancelTitleAnim(outer, inner)

      const overflow = outer.scrollWidth - outer.clientWidth

      if (overflow <= 0) return

      outer.style.textOverflow = 'clip'
      inner.classList.add('chat-title-inner--sliding')

      const speed = 40 // px per second
      const duration = Math.max(overflow / speed, 0.4)

      inner.style.transition = `transform ${duration}s linear`

      const rafId = requestAnimationFrame(() => {
        inner.style.transform = `translateX(-${overflow}px)`
      })

      outer._titleAnim = { rafId, cleanup: null }
    },

    onTitleMouseLeave (event) {
      const outer = event.currentTarget
      const inner = outer.querySelector('.chat-title-inner')

      this.cancelTitleAnim(outer, inner)

      const computed = getComputedStyle(inner).transform
      const hasMoved = computed && computed !== 'none' && !computed.endsWith('0, 0)')

      if (!hasMoved) {
        this.resetTitle(outer, inner)
        return
      }

      inner.style.transition = 'transform 0.3s ease'
      inner.style.transform = 'translateX(0)'

      const cleanup = () => {
        this.resetTitle(outer, inner)
        inner.removeEventListener('transitionend', cleanup)
      }

      inner.addEventListener('transitionend', cleanup, { once: true })
      outer._titleAnim = { rafId: null, cleanup }
    },

    cancelTitleAnim (outer, inner) {
      if (!outer._titleAnim) return

      if (outer._titleAnim.rafId) {
        cancelAnimationFrame(outer._titleAnim.rafId)
      }

      if (outer._titleAnim.cleanup) {
        inner.removeEventListener('transitionend', outer._titleAnim.cleanup)
      }

      outer._titleAnim = null
    },

    resetTitle (outer, inner) {
      outer.style.textOverflow = 'ellipsis'
      inner.classList.remove('chat-title-inner--sliding')
      inner.style.transition = ''
      inner.style.transform = ''
      outer._titleAnim = null
    }
  }
}
</script>

<style scoped>
.recent-chats {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  padding-top: 30px;

  min-height: 0;
  overflow: hidden;
}

.recent-body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;

  min-height: 0;
  overflow: hidden;
}

/* Header */

.recent-header {
  display: flex;
  align-items: center;

  flex: 0 0 auto;

  width: 100%;
  height: 28px;

  border: 0;

  background: transparent;

  color: #041f30;

  cursor: pointer;

  font-family: 'Inter';
  font-size: 12px;
  font-weight: 500;
  line-height: 21px;
  text-align: left;
  padding-bottom: 8px;
  padding-left: 20px;
  padding-right: 20px;
}

.recent-header:hover {
  color: #091f33;
}

.recent-header-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recent-chevron {
  flex: 0 0 auto;
  margin-left: 8px;
  color: #17334a !important;
}

/* Loading */

.loading-container {
  display: flex;
  align-items: flex-start;
  justify-content: center;

  flex: 1 1 auto;
  min-height: 0;

  padding-top: 18px;
}

/* List */

.chat-list {
  display: flex;
  flex: 0 1 auto;
  flex-direction: column;
  gap: 2px;

  min-height: 0;

  overflow-x: hidden;
  overflow-y: auto;

  /* Firefox: hide until hover/focus */
  scrollbar-width: none;
  scrollbar-color: transparent transparent;
}

.chat-list:hover,
.chat-list:focus-within {
  scrollbar-width: thin;
  scrollbar-color: #ccd5e4 transparent;
}

.chat-list::-webkit-scrollbar {
  width: 4px;
}

.chat-list::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 999px;
}

.chat-list::-webkit-scrollbar-track {
  background: transparent;
}

/* Session item */

.chat-item {
  position: relative;
  isolation: isolate;
  display: flex;
  align-items: center;

  height: 24px;
  width: 100%;
  min-width: 0;

  color: #17334a;
  cursor: pointer;
  background: transparent;
  border-radius: 8px;
  transition: background-color 0.15s ease;
  padding-left: 20px;
  padding-right: 8px;
}

.chat-item::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 8px;
  right: 8px;
  border-radius: 8px;
  background: #ffffff;
  opacity: 0;
  z-index: -1;
  transition: opacity 0.15s ease;
}

.chat-item:hover::before {
  opacity: 1;
}

.chat-item:has(.chat-delete-btn:hover)::before {
  opacity: 0 !important;
}

.chat-item:focus {
  outline: none;
}

.chat-item--active::before {
  opacity: 1;
}

.chat-item > * {
  position: relative;
  z-index: 1;
}

.chat-title-col {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.chat-title {
  display: block;
  position: relative;
  width: 100%;
  overflow: hidden;

  color: #041f30;
  font-size: 12px;
  font-weight: 400;
  line-height: 21px;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-title-inner {
  display: inline;
}

.chat-title-inner--sliding {
  display: inline-block;
  will-change: transform;
}

/* Delete button */

.chat-delete-col {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 2px;
}

.chat-delete-btn {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 24px;
  height: 24px;

  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  padding: 0;
  visibility: hidden;
  opacity: 0;
  transition: background-color 0.15s ease, opacity 0.15s ease;
}

.chat-item:hover .chat-delete-btn {
  visibility: visible;
  opacity: 1;
}

.chat-delete-btn:hover {
  background-color: #ffffff;
}

.chat-delete-icon {
  color: #63747D !important;
  transition: color 0.15s ease;
}

.chat-delete-btn:hover .chat-delete-icon {
  color: #041f30 !important;
}

/* Empty state */

.empty-state {
  color: #041f30;
  font-size: 12px;
  font-weight: 400;
  line-height: 21px;
  font-family: 'Inter';
  text-align: left;
  padding-left: 20px;
  padding-right: 20px;
}
</style>
